import { Stack, StackProps, Tooltip, Typography, TypographyProps } from '@mui/material';
import { omit } from 'lodash';
import { ReactNode } from 'react';

import { SensorIcon, SensorIconProps } from '@/components/data/SensorIcon';
import { Timestamp, TimestampProps } from '@/components/data/Timestamp';

type LabelRowProps = {
    label?: string;
    timestamp?: string | number | Date;
    timestampProps?: TimestampProps;
    labelProps?: TypographyProps;
    variant?: 'compact' | 'wide';
};

export type LabeledDataProps = {
    tooltip?: ReactNode;
    /**
     * Style used by the data row (not including the label)
     */
    dataSx?: StackProps['sx'];
} & LabelRowProps &
    StackProps;

export type IconDataProps = SensorIconProps & LabeledDataProps;

const LabelRow = ({ label, labelProps, timestamp, timestampProps, variant = 'compact' }: LabelRowProps) => {
    if (variant === 'wide') {
        return (
            <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                <Typography variant="minorLabel" {...labelProps}>
                    {label}
                </Typography>
                {timestamp && (
                    <Typography variant="minorLabel">
                        <Timestamp
                            value={timestamp}
                            sx={{ whiteSpace: 'nowrap' }}
                            {...{ type: 'distanceToNow', ...timestampProps }}
                        />
                    </Typography>
                )}
            </Stack>
        );
    }

    return (
        <Typography variant="minorLabel" {...labelProps}>
            {label}
            {timestamp && (
                <>
                    {' • '}
                    <Timestamp
                        value={timestamp}
                        sx={{ whiteSpace: 'nowrap' }}
                        {...{ type: 'distanceToNow', ...timestampProps }}
                    />
                </>
            )}
        </Typography>
    );
};

export const IconData = ({ children, variant = 'compact', ...props }: IconDataProps) => (
    <LabeledData variant={variant} {...omit(props, 'Icon', 'src')}>
        <SensorIcon {...props} />
        {children}
    </LabeledData>
);

export const LabeledData = ({
    children,
    tooltip,
    label,
    timestamp,
    timestampProps,
    labelProps,
    sx,
    dataSx,
    variant = 'compact',
    ...props
}: LabeledDataProps) => {
    return (
        <Stack {...props} sx={{ justifyContent: 'start', gap: '2px', ...sx }}>
            <LabelRow
                label={label}
                labelProps={labelProps}
                timestamp={timestamp}
                timestampProps={timestampProps}
                variant={variant}
            />
            <Tooltip title={tooltip}>
                <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', flexWrap: 'wrap', ...dataSx }}>
                    {children}
                </Stack>
            </Tooltip>
        </Stack>
    );
};
